import { useState } from 'react'
import { ChevronRight, MessageSquare } from 'lucide-react'
import { defaultInitialJourneys, onboardingScheduleChoices } from './onboardingSchedules'
import type { TestingScheduleInput } from '../schemas/testingSchedules'
import './OnboardingTestingChoice.css'

const TEST_LEVELS = [{ name: 'Smoke', journeys: 2 }, { name: 'Basic', journeys: 10 }, { name: 'Detailed', journeys: 50 }]

export default function OnboardingTestingChoice({ planKey, availableCredits, disabled, onSelect }: {
  planKey?: string | null
  availableCredits?: number
  disabled: boolean
  onSelect: (initialBudget: number, schedule: TestingScheduleInput | undefined, summary: string) => void
}) {
  const choices = onboardingScheduleChoices(planKey)
  const preferred = choices.find(choice => !choice.disabled)!
  const [selectedNow, setNow] = useState<number | null>(null)
  const now = selectedNow ?? defaultInitialJourneys(planKey, availableCredits) ?? 2
  const canAffordNow = availableCredits === undefined || 1 + now / 2 <= availableCredits
  const [future, setFuture] = useState(preferred.journeys)
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'once'>(preferred.schedule.cadence)
  const recurring = frequency !== 'once'
  const valid = !disabled && canAffordNow && (frequency !== 'daily' || !choices[0]!.disabled)
  const nowLevel = TEST_LEVELS.find(level => level.journeys === now)!.name
  const futureLevel = TEST_LEVELS.find(level => level.journeys === future)!.name.toLowerCase()
  const summary = recurring ? `${nowLevel} testing now, run ${frequency} ${futureLevel} tests.` : `${nowLevel} testing only now.`
  const selectLevel = (label: string, value: number, onChange: (value: number) => void) => (
    <select aria-label={label} value={value} disabled={disabled} onChange={event => onChange(Number(event.target.value))}>
      {TEST_LEVELS.map(level => <option key={level.name} value={level.journeys}>{level.name}</option>)}
    </select>
  )
  return <>
  <div className="chat-choice onboard-testing-choice" data-disabled={!valid || undefined}>
    <button type="button" className="onboard-testing-choice__submit" disabled={!valid}
      aria-label={`Choose testing plan: ${summary}`} onClick={() => {
        const selected = choices.find(choice => choice.id === frequency)
        onSelect(1 + now / 2, selected ? { ...selected.schedule, budget: future / 2 } : undefined, summary)
      }} />
    <span className="chat-choice__icon"><MessageSquare size={16} strokeWidth={2} aria-hidden /></span>
    <div className="chat-choice__body onboard-testing-choice__sentence"><span className="chat-choice__label">
      {selectLevel('Testing level now', now, setNow)} {recurring ? 'testing now, run ' : 'testing '}
      <select aria-label="How often to test" value={frequency} disabled={disabled} onChange={event => {
        const value = event.target.value
        if (value === 'daily' || value === 'weekly' || value === 'once') setFrequency(value)
      }}>
        <option value="daily" disabled={choices[0]!.disabled}>daily</option>
        <option value="weekly">weekly</option>
        <option value="once">only now</option>
      </select>{recurring && <> {selectLevel('Testing level on future runs', future, setFuture)} tests</>}.
    </span></div>
    <ChevronRight size={16} strokeWidth={2} className="chat-choice__chevron" aria-hidden />
  </div>
  {!canAffordNow && <p className="onboard-github__hint">There aren’t enough available credits for this initial testing level. Choose a smaller level or add credits to continue.</p>}
  </>
}
